import { Fragment, ReactNode } from "react";
import { Box, HStack, Link, Spinner, Text, VStack } from "@chakra-ui/react";
import { DripPartner } from "../../constants/partners";
import { PartnerTokenIcon } from "./PartnerTokenIcon";
import { ScratchWord } from "./ScratchWord";
import { DRIP_TEXT_LABEL, DRIP_TEXT_SECONDARY } from "./dripStyles";

interface DripPartnerHeroProps {
  partner: DripPartner;
  /** Resolved reward-token icon candidates (static override, then metadata). */
  iconSources: (string | undefined)[];
  apy: number;
  apyLoading: boolean;
}

// Strike-through "swap" device: the new word in the accent colour, followed by the
// original wording scratched out word-by-word — mirrors the base Drip copy while
// making the partner substitution obvious.
const SwapText = ({
  replacement,
  original,
  accent,
  strokeUrl,
}: {
  replacement: ReactNode;
  original: string;
  accent: string;
  strokeUrl: string;
}) => {
  const words = original.split(" ");
  return (
    <>
      <Text as="span" fontWeight="semibold" color={accent}>
        {replacement}
      </Text>{" "}
      <Text as="span" color={DRIP_TEXT_LABEL}>
        {words.map((word, index) => (
          <Fragment key={`${word}-${index}`}>
            <ScratchWord strokeUrl={strokeUrl}>{word}</ScratchWord>
            {index < words.length - 1 ? " " : null}
          </Fragment>
        ))}
      </Text>
    </>
  );
};

export const DripPartnerHero = ({
  partner,
  iconSources,
  apy,
  apyLoading,
}: DripPartnerHeroProps) => {
  const accent = partner.accent;
  const accentBg = (a: number) => `rgba(${partner.accentRgb}, ${a})`;

  // Pre-filled tweet with the live partner Drip page attached as the tweet card.
  const tweetUrl = `https://app.kyros.fi/${partner.slug}`;
  const tweetIntentUrl = `https://twitter.com/intent/tweet?text=${encodeURIComponent(
    partner.shareText
  )}&url=${encodeURIComponent(tweetUrl)}`;

  return (
    <VStack spacing={6} align="stretch" width="100%">
      <Box>
        <HStack spacing={3} align="center">
          <PartnerTokenIcon
            sources={iconSources}
            label={partner.tokenSymbol}
            accent={accent}
            boxSize="44px"
          />
          <Text
            fontWeight="semibold"
            fontSize="5xl"
            color="white"
            lineHeight="1.1"
            letterSpacing="-0.02em"
          >
            {partner.heroTitle ?? "Drip"}
          </Text>
        </HStack>
        <Text fontSize="md" color={DRIP_TEXT_SECONDARY} mt={2} lineHeight="1.6">
          The easiest way to DCA into{" "}
          <SwapText
            replacement={partner.tokenSymbol}
            original="any asset"
            accent={accent}
            strokeUrl={partner.scratchUrl}
          />
          . Sit back and enjoy the flow to your wallet.
        </Text>
      </Box>

      <Box
        bg={accentBg(0.06)}
        borderRadius="xl"
        p={4}
        border="1px solid"
        borderColor={accentBg(0.25)}
        boxShadow={`0 0 40px 4px ${accentBg(0.06)}`}
      >
        <VStack spacing={3} align="start">
          <HStack
            px={2.5}
            py={1}
            borderRadius="full"
            bg={accentBg(0.1)}
            border="1px solid"
            borderColor={accentBg(0.3)}
            spacing={2}
          >
            <Box
              className="partner-live-pulse"
              boxSize="7px"
              borderRadius="full"
              bg={accent}
              boxShadow={`0 0 8px 1px ${accentBg(0.8)}`}
              flexShrink={0}
            />
            <PartnerTokenIcon
              sources={iconSources}
              label={partner.tokenSymbol}
              accent={accent}
              boxSize="18px"
            />
            <Text fontSize="xs" fontWeight="bold" color={accent} letterSpacing="0.04em">
              {apyLoading ? (
                <Spinner size="xs" />
              ) : (
                `Earn up to ${(apy * 100).toFixed(2)}% on your SOL`
              )}
            </Text>
          </HStack>
          <Text fontSize="sm" color="whiteAlpha.900" lineHeight="1.5">
            Drip into{" "}
            <Text as="span" fontWeight="semibold" color={accent}>
              {partner.tokenSymbol}
            </Text>{" "}
            and let your SOL yield stream straight to your wallet.
          </Text>
          <Link
            href={tweetIntentUrl}
            isExternal
            fontSize="sm"
            fontWeight="semibold"
            color={accent}
            _hover={{ textDecoration: "underline" }}
          >
            {partner.shareCtaLabel}
          </Link>
        </VStack>
      </Box>
    </VStack>
  );
};
